import { useState, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material";
import { supabase } from "config/supabase";
import {
  PATIENT_SATISFACTION_QUESTIONS,
  DATA_QUALITY_QUESTIONS,
  QUATTRO_QUESTIONS,
  PATIENT_COMFORT_QUESTIONS,
  PREVENTION_QUESTIONS,
} from "../data/sbiCoreMetricMapping";
import {
  DEFAULT_VALUES_BY_SELECTION,
  DEFAULT_QUATTRO_METRICS as STATIC_QUATTRO_METRICS,
  WEEKLY_PATIENT_COMFORT_FOR_TRENDS,
} from "../data/sbiCoreDefaultValues";
import { getStartOfISOWeek, getEndOfISOWeek, getLastWeeks } from "../utils/weekFilter";

const FETCH_LIMIT = 50000;
const USE_DEFAULTS =
  String(
    process.env.REACT_APP_USE_DEFAULTS ?? process.env.USE_DEFAULTS ?? "",
  ).toLowerCase() === "true";

function aggregateResponses(rows) {
  const caseIds = new Set();
  const byQuestion = {};
  for (const r of rows) {
    caseIds.add(r.case_id);
    if (!byQuestion[r.question_id]) {
      byQuestion[r.question_id] = { cases: new Map(), totalResponses: 0 };
    }
    const q = byQuestion[r.question_id];
    q.totalResponses += 1;
    const existing = q.cases.get(r.case_id);
    const num = parseFloat(r.answer_value);
    const numeric = !Number.isNaN(num) ? num : null;
    const answerId = r.answer_id || null;
    if (!existing) {
      const answerIds = new Set();
      if (answerId) answerIds.add(answerId);
      q.cases.set(r.case_id, { numeric, raw: r.answer_value, answerIds });
    } else {
      if (answerId) existing.answerIds.add(answerId);
      if (numeric !== null && existing.numeric === null) {
        existing.numeric = numeric;
        existing.raw = r.answer_value;
      }
    }
  }
  return { totalCases: caseIds.size, byQuestion };
}

function computeMetricFromAggregate(entry, totalCases, byQuestion) {
  if (!entry.questionId || totalCases === 0) return null;
  const q = byQuestion[entry.questionId];
  if (!q || !q.cases) return null;
  const type = entry.type || "percent_below";
  const threshold = entry.threshold != null ? entry.threshold : 3;
  const answerIds =
    entry.answerIds || (entry.answerId ? [entry.answerId] : null);
  let count = 0;
  let sum = 0;
  let numCount = 0;
  for (const [, v] of q.cases) {
    if (
      type === "percent_answer" &&
      Array.isArray(answerIds) &&
      answerIds.length
    ) {
      const hasMatch = answerIds.some(
        (aid) => v.answerIds && v.answerIds.has(aid),
      );
      if (hasMatch) count += 1;
      continue;
    }
    if (type === "average") {
      const n = v.numeric;
      if (n !== null && !Number.isNaN(n)) {
        sum += n;
        numCount += 1;
      }
      continue;
    }
    const n = v.numeric;
    if (n === null) continue;
    if (type === "percent_below" && n <= threshold) count += 1;
    else if (type === "percent_above" && n >= threshold) count += 1;
  }
  if (type === "average") {
    const value = numCount ? sum / numCount : 0;
    return { value: Math.round(value * 100) / 100, count: numCount };
  }
  const value = (count / totalCases) * 100;
  return { value: Math.round(value * 100) / 100, count };
}

/**
 * Resolve dateFilter to a selection key: "all" or "week0".."week11" (week0 = first option in dropdown).
 */
function getSelectionKey(dateFilter) {
  if (dateFilter?.type !== "week" || dateFilter.week == null || dateFilter.year == null) {
    return "all";
  }
  const options = getLastWeeks(12);
  const idx = options.findIndex(
    (o) => o.week === dateFilter.week && o.year === dateFilter.year
  );
  if (idx < 0) return "all";
  return `week${idx}`;
}

/**
 * @param {Object} dateFilter - { type: 'all' } | { type: 'week', week: number, year: number }
 * @param {string} benchmarkMode - 'overall' | 'regional'
 */
export function useSbiCoreData(dateFilter = { type: "all" }, benchmarkMode = "overall") {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aggregate, setAggregate] = useState(null);

  useEffect(() => {
    if (USE_DEFAULTS) {
      setAggregate(null);
      setError(null);
      setLoading(false);
      return;
    }
    if (!supabase) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    let query = supabase
      .from("sbi_responses")
      .select("case_id, question_id, answer_id, answer_value")
      .limit(FETCH_LIMIT);

    if (
      dateFilter?.type === "week" &&
      dateFilter.week != null &&
      dateFilter.year != null
    ) {
      const start = getStartOfISOWeek(dateFilter.year, dateFilter.week);
      const end = getEndOfISOWeek(dateFilter.year, dateFilter.week);
      query = query
        .gte("created_at", start.toISOString())
        .lte("created_at", end.toISOString());
    }

    query.then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) {
        setError(err);
        setAggregate(null);
      } else {
        setAggregate(aggregateResponses(data || []));
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [dateFilter?.type, dateFilter?.week, dateFilter?.year]);

  return useMemo(() => {
    if (USE_DEFAULTS) {
      const isRegional = benchmarkMode === "regional";
      const isWeekSelected =
        dateFilter?.type === "week" &&
        dateFilter.week != null &&
        dateFilter.year != null;
      const defaultN = isRegional
        ? 420
        : isWeekSelected
          ? 120
          : 1380;
      const nLabel = isRegional
        ? `Regional n=${defaultN}`
        : `Alle n=${defaultN}`;

      const selectionKey = getSelectionKey(dateFilter);
      const selection = DEFAULT_VALUES_BY_SELECTION[selectionKey] ?? DEFAULT_VALUES_BY_SELECTION.all;

      const patientSatisfactionMetrics = selection.patientSatisfaction.map((m) => ({
        label: m.label,
        value: isRegional ? m.benchmarkRegional : m.benchmarkOverall,
        meValue: m.meValue,
      }));

      const dataQualityMetrics = selection.dataQuality.map((m) => ({
        label: m.label,
        value: isRegional ? m.benchmarkRegional : m.benchmarkOverall,
        meValue: m.meValue,
      }));

      const firstFour = selection.quattroMetrics.slice(0, 4).map((m) => ({
        label: m.label,
        color: m.color,
        value: m.meValue,
        benchmarkValue: isRegional ? m.benchmarkRegional : m.benchmarkOverall,
      }));
      const overallQuattro =
        firstFour.length === 4
          ? Math.round(
              (firstFour[0].value +
                firstFour[1].value +
                firstFour[2].value +
                firstFour[3].value) /
                4
            )
          : 25;
      const fifth = selection.quattroMetrics[4];
      const quattroMetrics = {
        overallValue: overallQuattro,
        metrics: [
          ...firstFour,
          {
            label: fifth?.label ?? "QUATTRO",
            color: fifth?.color ?? theme.palette?.medium?.main ?? "#546e7a",
            value: overallQuattro,
          },
        ],
      };

      const patientComfortMetrics = selection.patientComfort.map((m) => ({
        title: m.title,
        value: m.meValue,
        count: m.count,
        benchmarkValue: m.benchmarkOverall,
        ...(isRegional ? { regionalTarget: m.benchmarkRegional } : {}),
      }));

      const preventionMetrics = selection.prevention.map((m) => ({
        title: m.title,
        value: isRegional ? m.benchmarkRegional : m.benchmarkOverall,
        meValue: m.meValue,
      }));

      return {
        loading: false,
        error: null,
        totalCases: defaultN,
        nLabel,
        patientSatisfactionMetrics,
        dataQualityMetrics,
        quattroMetrics,
        patientComfortMetrics,
        preventionMetrics,
        weeklyPatientComfortForTrends: WEEKLY_PATIENT_COMFORT_FOR_TRENDS,
      };
    }

    const totalCases = aggregate?.totalCases ?? 0;
    const byQuestion = aggregate?.byQuestion ?? {};
    const nLabel = totalCases > 0 ? `Alle n=${totalCases}` : "Alle n=0";

    // When USE_DEFAULTS is false we only show DB-derived values; unmapped metrics get 0 (no fallback to DEFAULT_*)
    const noDataValue = 0;
    const noDataCount = 0;

    const patientSatisfaction = PATIENT_SATISFACTION_QUESTIONS.map((entry) => {
      const computed = entry.questionId
        ? computeMetricFromAggregate(entry, totalCases, byQuestion)
        : null;
      const v = computed != null ? computed.value : noDataValue;
      return {
        label: entry.label,
        value: v,
        meValue: v, // placeholder until per-user "Me" data is available
      };
    });

    const dataQuality = DATA_QUALITY_QUESTIONS.map((entry) => {
      const computed = entry.questionId
        ? computeMetricFromAggregate(entry, totalCases, byQuestion)
        : null;
      const v = computed != null ? computed.value : noDataValue;
      return {
        label: entry.label,
        value: v,
        meValue: v, // placeholder until per-user "Me" data is available
      };
    });

    const quattroList = QUATTRO_QUESTIONS.map((entry, i) => {
      const computed = entry.questionId
        ? computeMetricFromAggregate(entry, totalCases, byQuestion)
        : null;
      const def = STATIC_QUATTRO_METRICS[i];
      return {
        label: entry.label,
        value: computed != null ? computed.value : noDataValue,
        color: def?.color ?? theme.palette?.medium?.main ?? "#546e7a",
      };
    });

    const overallQuattro =
      quattroList.length === 5
        ? Math.round(
            (quattroList[0].value +
              quattroList[1].value +
              quattroList[2].value +
              quattroList[3].value) /
              4,
          )
        : 0;

    const patientComfort = PATIENT_COMFORT_QUESTIONS.map((entry) => {
      const computed = entry.questionId
        ? computeMetricFromAggregate(entry, totalCases, byQuestion)
        : null;
      return {
        title: entry.title,
        value: computed != null ? computed.value : noDataValue,
        count: computed != null ? computed.count : noDataCount,
      };
    });

    const prevention = PREVENTION_QUESTIONS.map((entry) => {
      const computed = entry.questionId
        ? computeMetricFromAggregate(entry, totalCases, byQuestion)
        : null;
      const v = computed != null ? computed.value : noDataValue;
      return {
        title: entry.title,
        value: v,
        meValue: v, // placeholder until per-user "Me" data is available
      };
    });

    return {
      loading,
      error,
      totalCases,
      nLabel,
      patientSatisfactionMetrics: patientSatisfaction,
      dataQualityMetrics: dataQuality,
      quattroMetrics: {
        overallValue: overallQuattro,
        metrics: quattroList,
      },
      patientComfortMetrics: patientComfort,
      preventionMetrics: prevention,
      weeklyPatientComfortForTrends: undefined,
    };
  }, [aggregate, loading, theme, dateFilter?.type, dateFilter?.week, dateFilter?.year, benchmarkMode]);
}
